import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { useReducedMotion } from './motion'
import type { Tone } from './Status'

// A plan against the calendar, with today on it.
//
// # WHY DATES DECIDE THE POSITIONS
//
// An exit plan's milestones are not evenly spaced: notice and access cut-off
// can be three days apart and the contract end a month later. Spacing them
// evenly would say the plan is nearly done when most of the time is still
// ahead, so each milestone sits where its date falls between the first and the
// last, and the green line runs to today.
//
// Dates are ISO days (YYYY-MM-DD) and are read as calendar days, never as
// instants, so a milestone does not move a day for a reader in another zone.
//
// Labels that would collide drop to a second row. Under 560px wide the plan
// becomes a plain vertical list with today in its place, which is also the
// order a screen reader hears it in at every width.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function dayNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return Number.NaN
  return Date.UTC(y, m - 1, d) / 86400000
}

// "15 Sep". Month names are fixed rather than taken from the locale, because
// some locales now write "Sept" and the plan should read the same everywhere.
export function dayMonth(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  if (!m || !d) return iso
  return String(d).padStart(2, '0') + ' ' + (MONTHS[m - 1] ?? '')
}

type Milestone = { key: string; label: string; date: string; passed: boolean }

export function Timeline({ milestones, today }: { milestones: Milestone[]; today: string }) {
  const reduced = useReducedMotion()
  const days = milestones.map((m) => dayNumber(m.date)).filter((n) => !Number.isNaN(n))
  const start = days.length ? Math.min(...days) : dayNumber(today)
  const end = days.length ? Math.max(...days) : start
  const span = Math.max(1, end - start)
  const at = (iso: string) => {
    const n = dayNumber(iso)
    if (Number.isNaN(n)) return 0
    return Math.max(0, Math.min(100, ((n - start) / span) * 100))
  }

  const todayAt = at(today)
  const [fill, setFill] = useState(reduced ? todayAt : 0)
  useEffect(() => {
    if (reduced) {
      setFill(todayAt)
      return
    }
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setFill(todayAt))
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [todayAt, reduced])

  // Sorted by date, with a level for each label: a label closer than 16% to
  // the one before it on the same level moves down a row.
  const sorted = [...milestones].sort((a, b) => dayNumber(a.date) - dayNumber(b.date))
  const lastOnLevel = [-100, -100]
  const placed = sorted.map((m) => {
    const pos = at(m.date)
    const level = pos - (lastOnLevel[0] ?? -100) < 16 && pos - (lastOnLevel[1] ?? -100) >= 16 ? 1 : 0
    lastOnLevel[level] = pos
    return { m, pos, level }
  })
  const rows = placed.some((p) => p.level === 1) ? 2 : 1
  const todayIndex = placed.findIndex((p) => dayNumber(p.m.date) > dayNumber(today))
  const align = (pos: number) => (pos < 10 ? ' kit-align-start' : pos > 90 ? ' kit-align-end' : '')

  const items: ReactNode[] = placed.map(({ m, pos, level }) => (
    <li
      key={m.key}
      className={'kit-mile' + (m.passed ? ' kit-passed' : '') + align(pos)}
      style={{ left: pos + '%', '--kit-level': level } as CSSProperties}
    >
      <b>{m.label}</b>
      <span className="kit-mono">{dayMonth(m.date)}</span>
      <span className="vh">{m.passed ? ', passed' : ', ahead'}</span>
    </li>
  ))
  const todayItem = (
    <li key="kit-today" className={'kit-today' + align(todayAt)} style={{ left: fill + '%' }}>
      <span>today {dayMonth(today)}</span>
    </li>
  )
  items.splice(todayIndex < 0 ? items.length : todayIndex, 0, todayItem)

  return (
    <div className="kit-timeline" style={{ '--kit-rows': rows } as CSSProperties}>
      <div className="kit-timeline-track" aria-hidden="true">
        <span className="kit-timeline-done" style={{ width: fill + '%' }} />
      </div>
      <ol className="kit-miles">{items}</ol>
    </div>
  )
}

// The activity variant: what happened, newest first, one line each with a
// coloured dot and when.
export function ActivityFeed({ items }: { items: { key: string; text: ReactNode; when: string; tone?: Tone }[] }) {
  return (
    <ul className="kit-feed">
      {items.map((it) => (
        <li key={it.key} className={'kit-feed-item kit-' + (it.tone ?? 'neutral')}>
          <i aria-hidden="true" />
          <span>
            {it.text}
            <small className="kit-mono">{it.when}</small>
          </span>
        </li>
      ))}
    </ul>
  )
}
